import defaultAvatar from "@assets/img/avatars/defaultAvatar.png";
import { config } from "config";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { api } from "@service/api";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { FaCamera, FaImage } from "react-icons/fa";

interface IProps {
  isOpen: boolean;
  currentAvatar?: string;
  onClose: () => void;
  onUpdated: (avatar: string) => void;
}

const acceptedMimeTypes = ["image/jpg", "image/jpeg", "image/png"];

export function ModalCustomerAvatar({
  isOpen,
  currentAvatar,
  onClose,
  onUpdated,
}: IProps) {
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();
  const inputRef = useRef<HTMLInputElement>(null);
  const [animate, setAnimate] = useState(false);
  const [preview, setPreview] = useState(currentAvatar || defaultAvatar);
  const [fileBase64, setFileBase64] = useState("");
  const [fileName, setFileName] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  function handleClose(force = false) {
    if (isSaving && !force) return;

    setAnimate(false);
    window.setTimeout(onClose, 250);
  }

  function readFileAsDataUrl(file: File) {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () =>
        reject(new Error("Não foi possível ler a imagem."));
      reader.readAsDataURL(file);
    });
  }

  async function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) return;

    if (!acceptedMimeTypes.includes(file.type)) {
      pushToastify({
        message: "Escolha uma imagem JPG, JPEG ou PNG.",
        type: "warning",
        theme: "dark",
      });
      return;
    }

    try {
      const dataUrl = await readFileAsDataUrl(file);
      setPreview(dataUrl);
      setFileBase64(dataUrl);
      setFileName(file.name);
    } catch {
      pushToastify({
        message: "Não foi possível carregar a imagem selecionada.",
        type: "error",
        theme: "dark",
      });
    }
  }

  async function handleSave() {
    if (!fileBase64 || isSaving) return;

    try {
      setIsSaving(true);

      const response = await api.post("/customer/update/avatar", {
        file: fileBase64,
      });

      onUpdated(response.data);
      pushToastify({
        message: "Foto de perfil atualizada com sucesso!",
        type: "success",
        theme: "dark",
      });
      handleClose(true);
    } catch (error: any) {
      pushToastify({
        message:
          error?.response?.data?.message ||
          error?.response?.data ||
          "Não foi possível atualizar a foto de perfil.",
        type: "error",
        theme: "dark",
      });
    } finally {
      setIsSaving(false);
    }
  }

  useEffect(() => {
    if (!isOpen) {
      setAnimate(false);
      return;
    }

    setPreview(currentAvatar || defaultAvatar);
    setFileBase64("");
    setFileName("");
    window.setTimeout(() => setAnimate(true), 10);

    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") handleClose();
    }

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, currentAvatar]);

  if (!isOpen) return null;

  return (
    <>
      <div
        className={`modal fade ${animate ? "show" : ""}`}
        style={{ display: "block" }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0 shadow">
            <div className="modal-header border-bottom">
              <div>
                <h5 className="modal-title mb-1">Alterar foto de perfil</h5>
                <p className="text-muted small mb-0">
                  Escolha uma imagem para atualizar seu avatar.
                </p>
              </div>
              <button
                type="button"
                className="btn-close"
                aria-label="Fechar"
                onClick={() => handleClose()}
              />
            </div>

            <div className="modal-body py-4">
              <div className="d-flex flex-column align-items-center text-center">
                <div
                  className="rounded-circle overflow-hidden border shadow-sm mb-3"
                  style={{ width: 164, height: 164 }}
                >
                  <img
                    src={preview}
                    alt="Prévia da foto de perfil"
                    className="w-100 h-100"
                    style={{ objectFit: "cover" }}
                  />
                </div>

                <input
                  ref={inputRef}
                  type="file"
                  accept="image/jpg,image/jpeg,image/png"
                  className="d-none"
                  onChange={handleFileChange}
                />

                <button
                  type="button"
                  className="btn btn-outline-primary d-inline-flex align-items-center gap-2"
                  onClick={() => inputRef.current?.click()}
                  disabled={isSaving}
                >
                  <FaImage />
                  Escolher imagem
                </button>

                <p className="small text-muted mb-0 mt-3">
                  JPG, JPEG ou PNG.
                  {fileName ? ` Arquivo selecionado: ${fileName}` : ""}
                </p>
              </div>
            </div>

            <div className="modal-footer border-top">
              <button
                type="button"
                className="btn btn-light"
                onClick={() => handleClose()}
                disabled={isSaving}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary d-inline-flex align-items-center gap-2"
                onClick={handleSave}
                disabled={!fileBase64 || isSaving}
              >
                <FaCamera />
                {isSaving ? "Salvando..." : "Salvar foto"}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className={`modal-backdrop fade ${animate ? "show" : ""}`} />
    </>
  );
}

